import React from "react";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

// Opens a file URL in a new tab
function openFile(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function isImageUrl(url) {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url) || url.startsWith("data:image");
}

// A single document/image tile
function FileTile({ url, label }) {
  if (!url) return null;
  const img = isImageUrl(url);
  return (
    <button style={F.tile} onClick={() => openFile(url)} title={`Open ${label} in new tab`}>
      {img ? (
        <img src={url} alt={label} style={F.thumb} onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <div style={F.iconBox}>
          <i className="bi bi-file-earmark-text-fill" style={{ fontSize: 28, color: PRIMARY }} />
        </div>
      )}
      <div style={F.tileLabel}>{label}</div>
      <div style={F.viewHint}><i className="bi bi-box-arrow-up-right" style={{ marginRight: 4 }} />View</div>
    </button>
  );
}

// Section heading
function Section({ icon, label }) {
  return (
    <div style={P.sectionLabel}>
      {icon && <i className={`bi ${icon}`} style={{ color: PRIMARY, marginRight: 6 }} />}
      {label}
    </div>
  );
}

// A single labelled field in the info grid
function Field({ label, value, full }) {
  if (value === null || value === undefined || value === "" || value === "—") {
    return null;
  }
  return (
    <div style={{ ...(full ? { gridColumn: "1 / -1" } : {}) }}>
      <div style={P.fieldLabel}>{label}</div>
      <div style={P.fieldValue}>{value}</div>
    </div>
  );
}

/**
 * Slide-out merchant detail panel.
 *
 * Props:
 *   merchant         – the full merchant profile object
 *   onClose          – called when the panel should be dismissed
 *   canViewSensitive – if true, shows ID docs, utility bill, account details
 *                      (true for sales-admin and EXMANAF admin; false for agents)
 */
export default function MerchantDetailPanel({ merchant: m, onClose, canViewSensitive = false }) {
  if (!m) return null;

  const isActive = m.subscriptionEnd && Date.now() < m.subscriptionEnd;
  const storeName = m.storeInfo?.storeName || m.fullname || m.email;

  // Build the location string for the map
  const locationStr = [
    m.location?.address || m.storeInfo?.storeAddress,
    m.location?.town,
    m.location?.lga || m.storeInfo?.storeLga,
    m.location?.state || m.storeInfo?.storeState,
  ].filter(Boolean).join(", ");

  // Collect all document URLs
  const docs = [];
  if (canViewSensitive) {
    const id = m.identification || {};
    if (id.id)        docs.push({ url: id.id,        label: id.idType ? `ID (${id.idType})` : "ID Card" });
    if (id.passport)  docs.push({ url: id.passport,  label: "Passport Photo" });
    if (id.signature) docs.push({ url: id.signature, label: "Signature" });
    const co = m.company || {};
    if (co.cacCert)  docs.push({ url: co.cacCert,  label: "CAC Certificate" });
    if (co.taxCert)  docs.push({ url: co.taxCert,  label: "Tax Certificate" });
    // image[] array — could be utility bill, extra docs, etc.
    const imgs = Array.isArray(m.image) ? m.image : [];
    imgs.forEach((img, i) => {
      const url = typeof img === "string" ? img : img?.Location || img?.url || "";
      if (url) docs.push({ url, label: `Uploaded file ${i + 1}` });
    });
  }

  const hasCompanyInfo = m.company && Object.values(m.company).some(Boolean);
  const acct = m.accountDetails || {};
  const hasAccountInfo = canViewSensitive && Object.values(acct).some(Boolean);

  return (
    <div style={P.overlay} onClick={onClose}>
      <div style={P.panel} onClick={(e) => e.stopPropagation()}>

        {/* ── Sticky header ── */}
        <div style={P.header}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={P.storeName}>{storeName}</div>
            <div style={P.email}>{m.email}</div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ ...P.badge, ...(isActive ? P.badgeGreen : P.badgeGrey) }}>
                {isActive ? "Active" : "Inactive"}
              </span>
              {m.plan && (
                <span style={{ ...P.badge, background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid #a7cc48" }}>
                  {m.plan}
                </span>
              )}
              {m.emailVerified && (
                <span style={{ ...P.badge, background: "rgba(34,197,94,0.12)", color: "#166534", border: "1px solid #86efac" }}>
                  <i className="bi bi-patch-check-fill" style={{ marginRight: 3 }} />Verified
                </span>
              )}
            </div>
          </div>
          <button style={P.closeBtn} onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div style={P.body}>

          {/* ── Bio / Personal ── */}
          <Section icon="bi-person-fill" label="Personal Info" />
          <div style={P.grid}>
            <Field label="Full Name"   value={m.fullname} />
            <Field label="Email"       value={m.email} />
            <Field label="Phone"       value={m.phone || m.storeInfo?.storePhone} />
            <Field label="Username"    value={m.username} />
            <Field label="Joined"      value={m.createdAt ? new Date(m.createdAt).toLocaleDateString() : null} />
            <Field label="Last Login"  value={m.lastLogin ? new Date(m.lastLogin).toLocaleString() : "Never"} />
            <Field label="Language"    value={m.language} />
            <Field label="Referred By (Code)" value={m.refBy} />
            <Field label="Own Ref Code"       value={m.refCode} />
          </div>

          {/* ── Store Info ── */}
          <Section icon="bi-shop" label="Store Information" />
          <div style={P.grid}>
            <Field label="Store Name"    value={m.storeInfo?.storeName} />
            <Field label="Store Email"   value={m.storeInfo?.storeEmail} />
            <Field label="Store Phone"   value={m.storeInfo?.storePhone} />
            <Field label="Store Address" value={m.storeInfo?.storeAddress} full />
            <Field label="LGA"           value={m.storeInfo?.storeLga || m.location?.lga} />
            <Field label="State"         value={m.storeInfo?.storeState || m.location?.state} />
            <Field label="Town"          value={m.location?.town} />
            <Field label="Full Address"  value={m.location?.address} full />
          </div>

          {/* ── Subscription ── */}
          <Section icon="bi-calendar-check" label="Subscription" />
          <div style={P.grid}>
            <Field label="Plan"             value={m.plan || "Free"} />
            <Field label="Level"            value={m.subscriptionLevel != null ? String(m.subscriptionLevel) : null} />
            <Field label="Status"           value={isActive ? "Active" : "Inactive"} />
            <Field label="Started"          value={m.subscriptionStart ? new Date(m.subscriptionStart).toLocaleDateString() : null} />
            <Field label="Expires"          value={m.subscriptionEnd ? new Date(m.subscriptionEnd).toLocaleDateString() : null} />
            <Field label="Cycle"            value={m.cycle ? `${m.cycle} month(s)` : null} />
          </div>

          {/* ── Wallet ── */}
          {(m.paystackWalletCreated || m.walletCreated || m.wallet) && (
            <>
              <Section icon="bi-wallet2" label="Wallet" />
              <div style={P.grid}>
                <Field label="Paystack Wallet" value={m.paystackWalletCreated ? "Created" : "Not created"} />
                <Field label="Wallet"          value={m.walletCreated ? "Created" : "Not created"} />
                {m.wallet && (
                  <>
                    <Field label="Account Number" value={m.wallet.accountNumber} />
                    <Field label="Bank"           value={m.wallet.bankName} />
                    <Field label="BVN Verified"   value={m.wallet.bvnVerified ? "Yes" : "No"} />
                    <Field label="Active"         value={m.wallet.active ? "Yes" : "No"} />
                    <Field label="Balance"        value={m.wallet.balance != null ? `₦${m.wallet.balance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : null} />
                  </>
                )}
              </div>
            </>
          )}

          {/* ── Company ── (only shown when data exists) */}
          {hasCompanyInfo && (
            <>
              <Section icon="bi-building" label="Business / Company" />
              <div style={P.grid}>
                <Field label="Company Name"    value={m.company.name} />
                <Field label="Address"         value={m.company.address} full />
                <Field label="RC Number"       value={m.company.rc} />
                <Field label="TIN"             value={m.company.tin} />
                <Field label="Nature of Biz"   value={m.company.nature} />
              </div>
            </>
          )}

          {/* ── Account Details (sensitive) ── */}
          {hasAccountInfo && (
            <>
              <Section icon="bi-bank" label="Bank Account Details" />
              <div style={P.sensitiveBox}>
                <div style={P.sensitiveNote}>
                  <i className="bi bi-shield-lock-fill" style={{ color: PRIMARY, marginRight: 6 }} />
                  Visible to authorised admins only
                </div>
                <div style={P.grid}>
                  <Field label="Bank"           value={acct.bank} />
                  <Field label="Account Name"   value={acct.accountName} />
                  <Field label="Account Number" value={acct.accountNo} />
                  <Field label="Branch"         value={acct.branch} />
                  <Field label="Payment Mode"   value={acct.paymentMode} />
                  {acct.bvn && (
                    <Field label="BVN" value={`${"*".repeat(7)}${acct.bvn.slice(-4)}`} />
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── KYC Documents (sensitive) ── */}
          {canViewSensitive && docs.length > 0 && (
            <>
              <Section icon="bi-folder2-open" label="Uploaded Documents" />
              <div style={P.sensitiveBox}>
                <div style={P.sensitiveNote}>
                  <i className="bi bi-shield-lock-fill" style={{ color: PRIMARY, marginRight: 6 }} />
                  Visible to authorised admins only
                </div>
                <div style={F.grid}>
                  {docs.map((d, i) => (
                    <FileTile key={i} url={d.url} label={d.label} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Map ── */}
          {locationStr && (
            <>
              <Section icon="bi-geo-alt-fill" label="Location Map" />
              <iframe
                title="Merchant location map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(locationStr)}&output=embed`}
                style={P.map}
                frameBorder="0"
                allowFullScreen
              />
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6, marginBottom: 20 }}>{locationStr}</div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const P = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" },
  panel: { background: "#fff", width: "min(560px, 96vw)", height: "100vh", overflowY: "auto", boxShadow: "-6px 0 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" },
  header: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 22px 16px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "#fff", zIndex: 2 },
  storeName: { fontSize: 17, fontWeight: 800, color: BG, lineHeight: 1.2 },
  email: { fontSize: 12, color: "#888", marginTop: 2 },
  badge: { borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 },
  badgeGreen: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid #a7cc48" },
  badgeGrey: { background: "#f5f5f5", color: "#888", border: "1px solid #e0e0e0" },
  closeBtn: { background: "none", border: "none", fontSize: 17, cursor: "pointer", color: "#999", padding: 4, marginLeft: "auto", flexShrink: 0 },
  body: { padding: "18px 22px", flex: 1 },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, marginTop: 20, display: "flex", alignItems: "center" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px 16px", marginBottom: 4 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
  fieldValue: { fontSize: 13, color: "#333", fontWeight: 500, wordBreak: "break-word" },
  sensitiveBox: { background: "#fffbf0", border: "1px solid #ffe58f", borderRadius: 8, padding: "12px 14px", marginBottom: 4 },
  sensitiveNote: { fontSize: 11, color: "#b45309", marginBottom: 10, display: "flex", alignItems: "center" },
  map: { width: "100%", height: 220, borderRadius: 8, border: "1px solid #eee" },
};

const F = {
  grid: { display: "flex", flexWrap: "wrap", gap: 12 },
  tile: { background: "#f9f9f9", border: "1.5px solid #eee", borderRadius: 8, padding: "8px 10px", cursor: "pointer", textAlign: "center", minWidth: 90, maxWidth: 120, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, transition: "border-color 0.2s" },
  thumb: { width: 70, height: 70, objectFit: "cover", borderRadius: 6, display: "block" },
  iconBox: { width: 70, height: 70, display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f0f0", borderRadius: 6 },
  tileLabel: { fontSize: 10, color: "#666", fontWeight: 600, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  viewHint: { fontSize: 10, color: PRIMARY, fontWeight: 700 },
};
